import Scrollable from "../../Shared/Edit/Scrollable";
import ComponentToolbar from "./ComponentToolbar/ComponentToolbar";
import componentPropertiesMap from './ComponentFormProperties.json';
import { useChangeBooleans, useComponentProperties, useFetchedValues, useReferences, useResetComponentProperties, useSaveComponent } from "../../../Hooks/Display/useComponentProperties";
import { useContext, useEffect, useState } from "react";
import { useSelectedComponent } from "../../../Hooks/Display/useComponents";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { DragAndDrop } from '@progress/kendo-react-common';
import ReferenceTable from "./ReferenceTable/ReferenceTable";
import { useSelectedPage, useUpdatePageComponent } from "../../../Hooks/Display/usePages";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import { getProjectRelations } from "../../Helpers/Schema/Schema";
import { getEntityPages } from "../../../API/Display/Display";
import { getPageByProperty } from "../utils";
import ComponentFormSkeleton from "../../Display/Shared/ComponentFormSkeleton";
import { useTranslation } from "react-i18next";
import { getEntities } from "../../../API/Entity/Entity";
import GenericTranslationDialog from "../../Entities/GenericTranslationDialog";
import { isLanguagesChanged } from "../utils";
import { GlobalContext } from "../../../Renderers/GlobalContext";

function Reference() {

    const formProperties = componentPropertiesMap.Reference;

    const projectId = usePropsStoreValue("projectId");
    const entityId = usePropsStoreValue("entityId");
    const project = usePropsStoreValue("project");

    const { subscription } = useContext(GlobalContext).state

    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const accessedFromDesigner = usePropsStoreValue("accessedFromDesigner");
    const resetContext = useResetComponentProperties();
    const saveComponent = useSaveComponent();
    const updatePageComponent = useUpdatePageComponent(selectedPage.parentId, selectedPage._id);

    const [{ referenceTable: fetchedValues }, { updateReferenceTableFetchedValue }] = useFetchedValues();
    const [properties, setProperties] = useComponentProperties();
    const [, setChangeProperty] = useChangeBooleans();
    const [state, dispatch] = useReferences();

    const { t, i18n } = useTranslation([
        "display/common",
        "display/component_toolbar",
        "display/tables/reference"
    ])

    useEffect(() => {
        getData();
    }, [])

    // componentWillUnmount
    useEffect(() => {
        return () => {
            resetContext();
        }
    }, [])

    useEffect(() => {
        if (!loading) {
            setPropertiesUsingEntities(fetchedValues.entities);
        }
    }, [selectedComponent])

    useEffect(() => {
        setIsChanged();
    }, [state.references])

    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (!loading) {
            setIsChanged();
        }
    }, [properties]);

    // state
    const [tabSelected, setTabSelected] = useState(0);
    const [showLabelTranslationDialog, setShowLabelTranslationDialog] = useState(false);

    // functions
    async function getData() {
        const entities = await getEntities(projectId);
        const relations = await getProjectRelations(projectId);
        for (const entity of entities) {
            const { data: pages } = await getEntityPages(entity._id);
            const entityRelations = relations.filter(relation => (
                (relation.fromEntity._id === entityId && relation.toEntity._id === entity._id) ||
                (relation.toEntity._id === entityId && relation.fromEntity._id === entity._id)
            ));
            const referencePages = [];
            for (const page of pages) {
                if (page.portalId === selectedPage.portalId && page.useAsReference && !page.showsRecordDetails) {
                    referencePages.push(page);
                }
            }
            entity.pages = referencePages;
            entity.relations = entityRelations;
            if (entity._id === entityId) {
                entity.pages.push({
                    _id: "page-attachment",
                    pageName: "Attachment"
                });
                entity.relations.push({
                    _id: "attachment",
                    identifier: "Attachment",
                    relationId: "Attachment"
                });
            }
        }
        setPropertiesUsingEntities(entities);
        const filteredEntities = entities.filter(entity => entity.relations.length > 0);
        updateReferenceTableFetchedValue("entities", filteredEntities);
        setChangeProperty("isGeneralPropertiesChanged", false);
        setLoading(false);
    }

    function setPropertiesUsingEntities(entities) {
        const references = selectedComponent.referredPages.map(reference => {
            switch (reference.relationType) {
                case "Attachment": {
                    const entity = entities.find(entity => entity._id === entityId);
                    const page = (
                        entity.pages.find(page => page._id === "page-attachment") ||
                        {
                            // id comes from backend. Check Reference.js in DisplayGeneration
                            _id: "page-attachment",
                            pageName: "Attachment"
                        }
                    );
                    debugger;
                    // relationId comes from backend. Check Reference.js in DisplayGeneration
                    const relation = (
                        entity.relations.find(relation => relation.relationId === "attachment") ||
                        { _id: "attachment", identifier: "Attachment", relationId: "attachment" }
                    );
                    return {
                        ...reference,
                        relationId: relation.relationId,
                        entity,
                        page,
                        relation
                    }
                }
                default: {
                    const entity = entities.find(entity => entity._id === reference.entityId);
                    const page = getPageByProperty(entity.pages, "_id", reference.pageId);
                    const relation = entity.relations.find(relation => relation._id === reference.relationId);
                    return {
                        ...reference,
                        entity,
                        page,
                        relation
                    }
                }
            }
        });
        for (const property of formProperties) {
            if (
                selectedComponent[property] !== undefined
            ) {
                setProperties(property, selectedComponent[property])
            }
        }
        dispatch.setReferences(references)
    }

    function handleLabel(event) {
        setProperties("label", event.target.value);
    }

    function setLabelTranslation(data) {
        setProperties("languages", data);
        setShowLabelTranslationDialog(false);
    }

    function setIsChanged() {
        let isChanged = false;
        for (const property of formProperties) {
            switch (property) {
                case "languages":
                    if (properties.languages && isLanguagesChanged(properties.languages, selectedComponent.languages)) {
                        isChanged = true;
                    }
                    break;
                default:
                    if (selectedComponent[property] !== properties[property]) {
                        isChanged = true;
                    }
                    break;
            }
            if (isChanged) {
                break;
            }
        }
        setChangeProperty("isGeneralPropertiesChanged", isChanged);
    }

    // handlers
    function handleTabSelect(event) {
        setTabSelected(event.selected);
    }

    async function handleSave() {
        const updatedComponent = await saveComponent();
        updatePageComponent(selectedComponent.id, updatedComponent);
    }

    if (loading) {
        return <ComponentFormSkeleton />
    }

    return (
        <div className={"h-100 d-flex flex-column line" + (!accessedFromDesigner && " runtimePopup")}>
            <ComponentToolbar onSave={handleSave} />
            <Scrollable noPadding={true}>
                <TabStrip className="flex-grow-1 h-100 edit1" selected={tabSelected} onSelect={handleTabSelect}>
                    <TabStripTab title={t("tabs.general", { ns: "display/common" })}>
                        <Scrollable>
                            <form className="p-2 edit2">
                                <div className="form-label" required>
                                    {t("form_labels.label", { ns: "display/common" })}
                                </div>
                                <input
                                    placeholder={t("form_placeholders.label", { ns: "display/common" })}
                                    className="form-control w-50"
                                    name="Label"
                                    maxLength={256}
                                    onChange={handleLabel}
                                    value={properties.label}
                                    disabled={selectedComponent.hidden}
                                />
                                {subscription.numOfLanguages > 1 &&
                                    <div className="col-12">
                                        <div className="form-label d-inline-block cursor-pointer" onClick={() => {
                                            setShowLabelTranslationDialog(true)
                                        }}>
                                            <span className="text-primary">
                                                {t("form_labels.label_translation", { ns: "display/common" })}
                                            </span>
                                        </div>
                                    </div>
                                }
                                {showLabelTranslationDialog && (
                                    <GenericTranslationDialog
                                        onClose={() => { setShowLabelTranslationDialog(false) }}
                                        onSave={setLabelTranslation}
                                        languages={properties.languages}
                                        project={project}
                                        fields={["label"]}
                                    />
                                )}
                            </form>
                        </Scrollable>
                    </TabStripTab>
                    <TabStripTab title={t("tabs.reference", { ns: "display/common" })}>
                        <DragAndDrop>
                            <ReferenceTable />
                        </DragAndDrop>
                    </TabStripTab>
                </TabStrip>
            </Scrollable>
        </div>
    )
}

export default Reference;